import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import type {
  MatterExpense,
  MatterLien,
  MatterSettlementCounter,
  MatterSettlementDemand,
  MatterSettlementOffer,
  MatterTrustCheck,
  MatterTrustCheckConnection,
  MedicalTreatmentBill,
} from "@/lib/types";
import { useSuspenseQuery } from "@tanstack/react-query";

type MatterTrustCheckConnectionRecord = MatterTrustCheckConnection & {
  expenseId?: string;
  lienId?: string;
  medicalTreatmentBillId?: string;
  settlementCounterId?: string;
  settlementDemandId?: string;
  settlementOfferId?: string;
  trustCheckId: string;
};

export type TrustCheckViewModel = MatterTrustCheck & {
  connections: (MatterTrustCheckConnectionRecord & {
    matterLien?: MatterLien;
    matterExpense?: MatterExpense;
    matterSettlementOffer?: MatterSettlementOffer;
    matterSettlementDemand?: MatterSettlementDemand;
    matterSettlementCounter?: MatterSettlementCounter;
    medicalTreatmentBills?: MedicalTreatmentBill;
  })[];
};

export const useTrustChecksViewModel = (matterId: string) => {
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;
  const expenses = matter.expenses;
  const liens = matter.liens;
  const medicalTreatmentBills = matter.medicalTreatmentBills;
  const settlementCounters = matter.settlementCounters;
  const settlementDemands = matter.settlementDemands;
  const settlementOffers = matter.settlementOffers;
  const trustCheckConnections =
    matter.trustCheckConnections as MatterTrustCheckConnectionRecord[];
  const trustChecks = matter.trustChecks;

  const connectionsWithItems = trustCheckConnections.map((connection) => {
    if (connection.lienId) {
      return {
        ...connection,
        lienId: connection.lienId,
        matterLien: liens.find((lien) => lien.id === connection.lienId),
      };
    }

    if (connection.expenseId) {
      return {
        ...connection,
        matterExpenseId: connection.expenseId,
        matterExpense: expenses.find(
          (expense) => expense.id === connection.expenseId,
        ),
      };
    }

    if (connection.settlementOfferId) {
      return {
        ...connection,
        matterSettlementOfferId: connection.settlementOfferId,
        matterSettlementOffer: settlementOffers.find(
          (offer) => offer.id === connection.settlementOfferId,
        ),
      };
    }

    if (connection.settlementCounterId) {
      return {
        ...connection,
        matterSettlementCounterId: connection.settlementCounterId,
        matterSettlementCounter: settlementCounters.find(
          (counter) => counter.id === connection.settlementCounterId,
        ),
      };
    }

    if (connection.settlementDemandId) {
      return {
        ...connection,
        matterSettlementDemandId: connection.settlementDemandId,
        matterSettlementDemand: settlementDemands.find(
          (demand) => demand.id === connection.settlementDemandId,
        ),
      };
    }

    if (connection.medicalTreatmentBillId) {
      const medicalTreatmentBill = medicalTreatmentBills.find(
        (bill) => bill.id === connection.medicalTreatmentBillId,
      );
      return {
        ...connection,
        matterMedicalTreatmentBill: medicalTreatmentBill,
        medicalTreatmentBills: medicalTreatmentBill,
      };
    }

    return connection;
  });

  return trustChecks.map((trustCheck) => {
    const connections = connectionsWithItems.filter(
      (connection) => connection.trustCheckId === trustCheck.id,
    );
    return {
      ...trustCheck,
      connections,
    };
  }) as TrustCheckViewModel[];
};
